import { clamp, toFinite } from '../math/MathUtils';

/**
 * Upper bound applied to the device pixel ratio. Toddler tablets often report 3 or 4, which triples the
 * fill cost for a difference nobody can see on chunky cartoon art.
 */
export const MAX_PIXEL_RATIO = 2;

const MIN_PIXEL_RATIO = 1;
const MIN_BACKING_STORE_DIMENSION = 1;

/** A canvas' layout size in CSS pixels together with the backing store size in physical pixels. */
export interface CanvasSize {
  /** Width of the canvas element as laid out by CSS. */
  readonly cssWidth: number;
  /** Height of the canvas element as laid out by CSS. */
  readonly cssHeight: number;
  /** Width of the drawing buffer in physical pixels. */
  readonly pixelWidth: number;
  /** Height of the drawing buffer in physical pixels. */
  readonly pixelHeight: number;
  /** Ratio actually applied, after clamping the display's device pixel ratio. */
  readonly pixelRatio: number;
}

/**
 * Anything that can report the current canvas size.
 *
 * {@link CanvasSurface} is the production implementation; depending on this instead lets the render
 * system be driven by a plain object in tests, with no DOM anywhere in the suite.
 */
export interface CanvasSizeSource {
  /** The current layout and backing store size. */
  readonly size: CanvasSize;
}

/**
 * Derives a canvas' backing store size from its CSS size and the display's device pixel ratio.
 *
 * Pure so the sizing policy can be unit tested without a DOM. Non-finite or negative inputs are
 * treated as their nearest sane value rather than propagating NaN into the drawing buffer.
 */
export function computeCanvasSize(
  cssWidth: number,
  cssHeight: number,
  devicePixelRatio: number,
  maxPixelRatio: number = MAX_PIXEL_RATIO,
): CanvasSize {
  const safeCssWidth = toNonNegative(cssWidth);
  const safeCssHeight = toNonNegative(cssHeight);
  const upperBound = Math.max(MIN_PIXEL_RATIO, toFinite(maxPixelRatio, MAX_PIXEL_RATIO));
  const pixelRatio = clamp(toFinite(devicePixelRatio, MIN_PIXEL_RATIO), MIN_PIXEL_RATIO, upperBound);

  return {
    cssWidth: safeCssWidth,
    cssHeight: safeCssHeight,
    pixelWidth: toBackingStoreDimension(safeCssWidth * pixelRatio),
    pixelHeight: toBackingStoreDimension(safeCssHeight * pixelRatio),
    pixelRatio,
  };
}

/** Reports whether two canvas sizes describe the same layout and backing store. */
export function canvasSizeEquals(first: CanvasSize, second: CanvasSize): boolean {
  return (
    first.cssWidth === second.cssWidth &&
    first.cssHeight === second.cssHeight &&
    first.pixelWidth === second.pixelWidth &&
    first.pixelHeight === second.pixelHeight &&
    first.pixelRatio === second.pixelRatio
  );
}

function toBackingStoreDimension(value: number): number {
  return Math.max(MIN_BACKING_STORE_DIMENSION, Math.round(value));
}

function toNonNegative(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}
